import { useState, useMemo, forwardRef, useCallback, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { theme } from '../styles/theme';
import { getExpenditureTypeColor, getSubTypeColor, SONSTIGES_COLOR, SONSTIGES_ID } from '../utils/colors';
import { orderStackTypes } from '../utils/stackOrder';
import {
  HAUSHALTSTECHNIK_TYPE,
  HAUSHALTSTECHNIK_POS,
  HAUSHALTSTECHNIK_NEG,
  mergeHaushaltstechnikEntries,
} from '../utils/haushaltstechnik';
import { pchipResample, type ResampleRow } from '../utils/resample';
import { formatGdpPercentage, formatGdpPercentageTick, formatTooltipValue, createAxisFormatter, getAxisUnit } from '../utils/formatters';
import { buildFilterInfoText, type ActiveFilterValues, type FilterOptionsLookup } from '../utils/filterInfo';
import { useFlushLeftYAxisWidth } from '../hooks/useFlushLeftYAxisWidth';
import { MultiValueTooltip, IstSollTooltip } from './Tooltip';
import { ExportDropdown } from './ExportDropdown';
import { EmbedButton } from './EmbedButton';
import { TitlePointPopover } from './TitlePointPopover';
import { SonstigesTitlePicker } from './SonstigesTitlePicker';
import { exportChartAsPng, exportChartAsSvg, type ChartExportOptions, type LegendItem } from '../utils/chartExport';
import { Section, CardSubtitle, FlexRow, Button, Spinner, LoadingOverlay } from '../styles/components';
import { categoryLabel, useT } from '../i18n';
import type { BudgetApiClient } from '../api/client';
import type { ExpenditureTypeData, TitleData, TitleYearMeta, SelectedTitle, DisplayUnit, GdpData, BudgetTotalData, FilterState } from '../types';

// Colors for single-title Ist/Soll comparison (DZ catalog: black + red accent)
const IST_LINE_COLOR = '#000000';
const SOLL_LINE_COLOR = '#DA454E';

// Touch / no-hover devices. Mirrors InvestmentMap.isCoarsePointer: on these a
// tap on a title data point should first only preview the tooltip and open the
// detail popover on a deliberate second tap, instead of doing both at once.
function isCoarsePointer(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: none), (pointer: coarse)').matches;
}

// Haushaltstechnik constants and merge logic now live in utils/haushaltstechnik.ts
// so the merge is unit-tested. Don't reintroduce inline copies here — the dataKey
// naming pattern is load-bearing for the tooltip merger.

const ChartContainer = styled.div`
  & *:focus {
    outline: none;
  }
  & svg, & path, & g {
    outline: none;
  }
`;

// Unit label above the y-axis — publishing chart engine: ABC Social 13px
// black, written ONCE as a caption anchored at the axis line and extending
// right into the plot (createChart.js drawYAxis: x=0, text-anchor start).
const AxisUnitLabel = styled.div`
  font-family: var(--sans-stack);
  font-size: 13px;
  color: #000000;
  text-align: left;
  white-space: nowrap;
  margin-bottom: 2px;
`;

// Icon-only back affordance built on the shared `.button` family (ghost
// variant) rather than the bespoke IconButton, so it inherits the shared
// button sizing/hover/focus treatment. Trimmed to a square padding and given
// a 20px glyph to read as a compact icon button.
const BackButton = styled(Button)`
  &.button {
    padding: ${theme.spacing.xs};
    color: ${theme.colors.primary};
  }
  .icon {
    font-size: 20px;
    display: block;
  }
`;

const ActiveFilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.xl};
  background: rgba(24, 28, 68, 0.1);
`;

const ChipText = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.primary};
`;

const ClearChipButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 14px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: ${theme.colors.primary};
  transition: all 0.2s;

  &:hover {
    background: rgba(24, 28, 68, 0.2);
  }
`;

// Breadcrumb header — same shape as BudgetTable's: last item = serif-l
// chart title (matches dz-shared `@mixin serif-l`); earlier crumbs = small
// sans muted; sans separator. Keeps chart title visually aligned with the
// table title elsewhere in the dashboard.
const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

const BreadcrumbItem = styled.span<{ isLast: boolean; isClickable: boolean }>`
  ${props => props.isLast ? `
    font-family: var(--serif-stack);
    font-size: 1.875rem;
    line-height: 1.15;
    letter-spacing: -0.02em;
    font-weight: normal;
    color: var(--color-text-default);
  ` : `
    font-family: var(--sans-stack);
    font-size: 1rem;
    line-height: 1.2;
    color: var(--color-text-muted);
  `}
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};

  ${props => props.isClickable && `
    &:hover {
      color: var(--primary-color);
      text-decoration: underline;
    }
  `}
`;

const BreadcrumbSeparator = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  color: var(--color-text-muted);
  margin: 0 ${theme.spacing.xs};
`;

const FilterInfoLine = styled.div`
  font-family: var(--sans-stack);
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-top: ${theme.spacing.xs};
`;

// Legend pills — publishing chart engine / Figma: white chips, 19px radius,
// 5px/9px padding, 26px height, 10px round dot, 13px black ABC Social label.
// Bottom legends are LEFT-aligned against the plot/title edge (createLegend.js
// Figma rule); only top legends are centered.
const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  margin-top: ${theme.spacing.md};
`;

const LegendItem = styled.div<{ dimmed?: boolean; isClickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border-radius: 19px;
  background: #FFFFFF;
  cursor: ${props => props.isClickable === false ? 'default' : 'pointer'};
  opacity: ${props => props.dimmed ? 0.4 : 1};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${props => props.isClickable === false ? props.dimmed ? 0.4 : 1 : 1};
  }
`;

const LegendDot = styled.span<{ color: string; dashed?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => props.dashed ? 'transparent' : props.color};
  ${props => props.dashed && `
    border: 1px dashed #000000;
  `}
`;

const LegendLabel = styled.span`
  font-family: var(--sans-stack);
  font-size: 13px;
  line-height: 16px; /* 5+16+5 = 26px chip height per Figma layout_LZS74O */
  color: #000000;
`;

// Chevron hint on the "Sonstiges" chip signalling it opens the title picker.
const LegendChevron = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  color: var(--color-text-muted);
  .icon { display: block; }
`;

// "Remove" affordance on a broken-out title chip — folds it back into Sonstiges.
const LegendRemove = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 1px;
  border-radius: 50%;
  font-size: 13px;
  color: var(--color-text-muted);
  transition: background 0.15s ease, color 0.15s ease;
  .icon { display: block; }

  &:hover {
    background: rgba(24, 28, 68, 0.12);
    color: var(--color-text-default);
  }
`;

interface ExpenditureAreaChartProps {
  data: ExpenditureTypeData[];
  subData?: ExpenditureTypeData[] | null;
  titleData?: TitleData[] | null;
  subColorMap?: Map<string, string> | null;
  titleColorMap?: Map<string, string> | null;
  isLoading?: boolean;
  isLoadingSubData?: boolean;
  isLoadingTitleData?: boolean;
  drillDownType: string | null;
  drillDownSubType: string | null;
  selectedTitle?: SelectedTitle | null;
  // Title ids broken out of "Sonstiges" into their own series, and the toggle to
  // add/remove them. `filters` is forwarded to the picker so it can fetch the
  // list of titles currently inside Sonstiges.
  brokenOutTitleIds?: string[];
  onToggleTitleBreakout?: (id: string) => void;
  filters?: FilterState;
  onDrillDown: (type: string) => void;
  onDrillDownSub: (subType: string) => void;
  onDrillUp: () => void;
  onFilterBySubType?: (subType: string) => void;
  onFilterByTitle?: (id: string, zweckbestimmung: string, titelFunktion?: string, prefix?: string) => void;
  onNavigateToRoot?: () => void;
  onNavigateToLevel2?: () => void;
  onDeselectTitle?: () => void;
  activeFilter?: string | null;
  onClearFilter?: () => void;
  budgetType: 'E' | 'A';
  displayUnit?: DisplayUnit;
  gdpData?: GdpData[] | null;
  budgetTotals?: BudgetTotalData[] | null;
  activeFilters?: ActiveFilterValues;
  filterOptions?: FilterOptionsLookup | null;
  yearsWithIst?: Set<number>;
  apiClient?: BudgetApiClient;
  /**
   * false locks the chart down for an embed: areas and legend entries stop
   * inviting clicks that would go nowhere, and the subtitle drops its
   * "Klicken zum ..." instructions. Tooltips and hover highlighting stay —
   * they read the chart rather than navigate it.
   */
  interactive?: boolean;
  /** Opens the embed-snippet dialog; omitted inside an embed. */
  onEmbed?: () => void;
  /**
   * Hide the PNG/SVG export menu. An embed drops it so the frame needs no icon
   * font at all — see the embed stylesheet, which ships no Material Symbols.
   */
  showExport?: boolean;
}

export const ExpenditureAreaChart = forwardRef<HTMLDivElement, ExpenditureAreaChartProps>(
  function ExpenditureAreaChart({
    data,
    subData,
    titleData,
    subColorMap: propsSubColorMap,
    titleColorMap: propsTitleColorMap,
    isLoading,
    isLoadingSubData,
    isLoadingTitleData,
    drillDownType,
    drillDownSubType,
    selectedTitle,
    brokenOutTitleIds,
    onToggleTitleBreakout,
    filters,
    onDrillDown,
    onDrillDownSub,
    onDrillUp,
    onFilterByTitle,
    onNavigateToRoot,
    onNavigateToLevel2,
    onDeselectTitle,
    activeFilter,
    onClearFilter,
    budgetType,
    displayUnit = 'absolute',
    gdpData,
    budgetTotals,
    activeFilters,
    filterOptions,
    yearsWithIst,
    apiClient,
    interactive = true,
    onEmbed,
    showExport = true,
  }: ExpenditureAreaChartProps, ref) {
  const m = useT();
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);
  const [clickedPoint, setClickedPoint] = useState<{
    year: number;
    sollValue: number;
    istValue: number | null;
    anchorX: number;
    anchorY: number;
  } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Anchor for the "Sonstiges" title picker (client coords of the opening click);
  // null when closed. The picker lists the titles inside Sonstiges and toggles
  // their break-out into their own series.
  const [pickerAnchor, setPickerAnchor] = useState<{ x: number; y: number } | null>(null);
  // Last pointer position over the chart — recharts' Area onClick doesn't hand us
  // a native event reliably, so we anchor the picker to the tracked coords.
  const lastPointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Touch only: the year whose tooltip is currently "armed". First tap on a
  // title data point shows its tooltip (via Recharts) and arms the year; a
  // second tap on the same year opens the detail popover. The arm auto-clears
  // after a beat so a later tap counts as a fresh first tap. Unused on fine
  // pointers, where a single click opens the popover immediately.
  const armedYearRef = useRef<number | null>(null);
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (armedTimerRef.current) clearTimeout(armedTimerRef.current); }, []);

  // Helper function to convert value based on display unit
  const convertValue = useCallback((value: number, year: number): number => {
    if (displayUnit === 'absolute') return value;
    if (displayUnit === 'budget_percent') {
      // Denominator parity: this picks ist when total_ist > 0, otherwise soll.
      // The chart's per-category numerator (in mainChartInfo / subChartInfo /
      // titleChartInfo) uses the same threshold via `yearsWithIst.has(year)`,
      // which is computed from this same budgetTotals data. So for any given
      // year the numerator and denominator are always both ist or both soll —
      // they don't get mixed.
      //
      // The fallthrough `return value` below covers two transient cases:
      //   1. budgetTotals not yet loaded (handled by parent's loading gate)
      //   2. budgetTotals errored (handled by parent's auto-snap to absolute)
      // In both cases the caller has already moved displayUnit away from
      // budget_percent, so this branch isn't actually reached at steady state.
      // The remaining edge case — year present in chart data but missing from
      // budgetTotals — is extremely rare (would need data-source divergence
      // between /api/budget and /api/budget-totals) and would render that
      // year as raw thousands. Acceptable until/unless it bites.
      if (!budgetTotals) return value;
      const yearTotal = budgetTotals.find(t => t.year === year);
      if (!yearTotal) return value;
      const denom = yearTotal.total_ist != null && yearTotal.total_ist > 0
        ? yearTotal.total_ist : yearTotal.total_soll;
      if (denom === 0) return 0;
      return (value / denom) * 100;
    }
    // gdp_percent mode: same loading/error story as budget_percent above.
    if (!gdpData) return value;
    const gdp = gdpData.find(g => g.year === year);
    if (!gdp) return value;
    // Formula: (budget_value_thousands / (gdp_value_millions * 1000)) * 100
    return (value / (gdp.value * 1000)) * 100;
  }, [displayUnit, gdpData, budgetTotals]);

  // Use centralized yearsWithIst from parent, with fallback
  const globalYearsWithIst = yearsWithIst ?? new Set<number>();

  // valueFormatter is computed after displayData, see below

  // Process main data
  const mainChartInfo = useMemo(() => {
    // In budget_percent mode, exclude Haushaltstechnik & Sonderposten
    const filteredData = displayUnit === 'budget_percent'
      ? data.filter(d => d.expenditure_type !== HAUSHALTSTECHNIK_TYPE)
      : data;
    const years = [...new Set(filteredData.map(d => d.year))].sort((a, b) => a - b);
    const expenditureTypes = [...new Set(filteredData.map(d => d.expenditure_type))];

    // Use global Ist year detection (not affected by sidebar filters)
    const yearsWithIst = globalYearsWithIst;
    const sollYears = years.filter(y => !yearsWithIst.has(y));

    // Sort by value in the latest available year, mirroring the displayed
    // value: Ist if that year has Ist data, Soll otherwise.
    const latestYear = years.at(-1);
    const latestUsesIst = latestYear != null && yearsWithIst.has(latestYear);
    const typeLatestValues = new Map<string, number>();
    filteredData.forEach(d => {
      if (d.year !== latestYear) return;
      const value = latestUsesIst ? d.total_ist : d.total_soll;
      typeLatestValues.set(d.expenditure_type, value ?? 0);
    });

    // Fixed base bands, then by size, Haushaltstechnik last — see stackOrder.
    const finalSortedTypes = orderStackTypes(
      expenditureTypes,
      type => typeLatestValues.get(type) ?? 0,
    );

    // Assign colors
    const colorMap = new Map<string, string>();
    finalSortedTypes.forEach(type => {
      colorMap.set(type, getExpenditureTypeColor(type));
    });

    // Create chart data
    const chartData = years.map(year => {
      const useIst = yearsWithIst.has(year);
      const yearData: Record<string, number> = { year };
      filteredData
        .filter(d => d.year === year)
        .forEach(d => {
          if (useIst && d.total_ist == null) return;
          // total_soll can be null (SUM() of all-null source rows). Coalesce to 0
          // so the stacked value is always a number — a null in d3.stack's running
          // sum becomes NaN and corrupts every layer above it.
          const rawValue = useIst ? (d.total_ist ?? 0) : (d.total_soll ?? 0);
          const value = convertValue(rawValue, year);
          if (d.expenditure_type === HAUSHALTSTECHNIK_TYPE) {
            yearData[HAUSHALTSTECHNIK_POS] = Math.max(value, 0);
            yearData[HAUSHALTSTECHNIK_NEG] = Math.min(value, 0);
          } else {
            yearData[d.expenditure_type] = value;
          }
        });
      return yearData;
    });

    // Smooth transition: inject 0 in the last Ist year for types that were
    // skipped (NULL Ist) but appear in Soll years, so the area ramps from 0.
    const lastIstYear = years.filter(y => yearsWithIst.has(y)).at(-1);
    const firstSollYear = sollYears[0];
    if (lastIstYear != null && firstSollYear != null) {
      const lastIstEntry = chartData.find(d => d.year === lastIstYear);
      const firstSollEntry = chartData.find(d => d.year === firstSollYear);
      if (lastIstEntry && firstSollEntry) {
        for (const key of Object.keys(firstSollEntry)) {
          if (key !== 'year' && !(key in lastIstEntry)) {
            lastIstEntry[key] = 0;
          }
        }
      }
    }

    return { years, yearsWithIst, sollYears, sortedTypes: finalSortedTypes, colorMap, chartData };
  }, [data, displayUnit, convertValue, globalYearsWithIst]);

  // Process sub-data for drill-down
  const subChartInfo = useMemo(() => {
    if (!subData || subData.length === 0) return null;

    const subYears = [...new Set(subData.map(d => d.year))].sort((a, b) => a - b);
    const subTypes = [...new Set(subData.map(d => d.expenditure_type))];

    // Use global Ist year detection (not affected by sidebar filters)
    const subYearsWithIst = globalYearsWithIst;
    const subSollYears = subYears.filter(y => !subYearsWithIst.has(y));

    // Sort by value in the latest available year, mirroring the displayed
    // value: Ist if that year has Ist data, Soll otherwise.
    const latestSubYear = subYears.at(-1);
    const latestSubUsesIst = latestSubYear != null && subYearsWithIst.has(latestSubYear);
    const subTypeLatestValues = new Map<string, number>();
    subData.forEach(d => {
      if (d.year !== latestSubYear) return;
      const value = latestSubUsesIst ? d.total_ist : d.total_soll;
      subTypeLatestValues.set(d.expenditure_type, value ?? 0);
    });

    const sortedSubTypes = [...subTypes].sort((a, b) => {
      return (subTypeLatestValues.get(b) || 0) - (subTypeLatestValues.get(a) || 0);
    });

    // Assign colors - use passed color map from parent for consistency, with local fallback
    const localSubColorMap = new Map<string, string>();
    sortedSubTypes.forEach((type, index) => {
      localSubColorMap.set(type, propsSubColorMap?.get(type) ?? getSubTypeColor(index));
    });

    // Create chart data
    const subChartData = subYears.map(year => {
      const useIst = subYearsWithIst.has(year);
      const yearData: Record<string, number> = { year };
      subData
        .filter(d => d.year === year)
        .forEach(d => {
          const rawValue = useIst ? (d.total_ist ?? 0) : (d.total_soll ?? 0);
          yearData[d.expenditure_type] = convertValue(rawValue, year);
        });
      return yearData;
    });

    // Fill missing values with zeros for stable stacking
    const allSubTypes = sortedSubTypes;
    const filledSubChartData = subChartData.map(yearData => {
      const filledData = { ...yearData };
      allSubTypes.forEach(type => {
        if (filledData[type] === undefined) {
          filledData[type] = 0;
        }
      });
      return filledData;
    });

    return {
      years: subYears,
      yearsWithIst: subYearsWithIst,
      sollYears: subSollYears,
      sortedTypes: sortedSubTypes,
      colorMap: localSubColorMap,
      chartData: filledSubChartData,
    };
  }, [subData, propsSubColorMap, convertValue, globalYearsWithIst]);

  // Process title data for third drill-down level
  // Key by id (not zweckbestimmung) to consolidate entries with same id but different names
  const titleChartInfo = useMemo(() => {
    if (!titleData || titleData.length === 0) return null;

    const titleYears = [...new Set(titleData.map(d => d.year))].sort((a, b) => a - b);

    // Create id -> year -> name map for year-specific names
    const idYearToNameMap = new Map<string, Map<number, string>>();
    // Year-specific Einzelplan/Kapitel for tooltips. Both can change year over
    // year for the same id, so we look them up per (id, year) when rendering.
    const idYearToMetaMap = new Map<string, Map<number, TitleYearMeta>>();
    titleData.forEach(d => {
      if (!idYearToNameMap.has(d.id)) {
        idYearToNameMap.set(d.id, new Map());
      }
      idYearToNameMap.get(d.id)!.set(d.year, d.zweckbestimmung);

      if (!idYearToMetaMap.has(d.id)) {
        idYearToMetaMap.set(d.id, new Map());
      }
      idYearToMetaMap.get(d.id)!.set(d.year, {
        einzelplan: d.einzelplan,
        einzelplanText: d.einzelplan_text,
        kapitel: d.kapitel,
        kapitelText: d.kapitel_text,
      });
    });

    // Create id -> display name map (latest name for legend display)
    const idToNameMap = new Map<string, string>();
    const idToTitelFunktionMap = new Map<string, string>();
    const idToPrefixMap = new Map<string, string>();
    titleData.forEach(d => {
      // Use the name from the most recent year
      const existingYear = idToNameMap.has(d.id)
        ? Math.max(...Array.from(idYearToNameMap.get(d.id)!.keys()).filter(y => y <= d.year))
        : 0;
      if (d.year >= existingYear) {
        idToNameMap.set(d.id, d.zweckbestimmung);
        if (d.titel_funktion) idToTitelFunktionMap.set(d.id, d.titel_funktion);
        if (d.prefix) idToPrefixMap.set(d.id, d.prefix);
      }
    });

    // Use global Ist year detection (not affected by sidebar filters)
    const titleYearsWithIst = globalYearsWithIst;
    const titleSollYears = titleYears.filter(y => !titleYearsWithIst.has(y));

    // Sort by value in the latest available year (key by id), mirroring the
    // displayed value: Ist if that year has Ist data, Soll otherwise.
    const latestTitleYear = titleYears.at(-1);
    const latestTitleUsesIst = latestTitleYear != null && titleYearsWithIst.has(latestTitleYear);
    const titleLatestValues = new Map<string, number>();
    titleData.forEach(d => {
      if (d.year !== latestTitleYear) return;
      const value = latestTitleUsesIst ? d.total_ist : d.total_soll;
      titleLatestValues.set(d.id, value ?? 0);
    });

    // Get unique ids and sort by latest-year value
    const sortedIds = [...new Set(titleData.map(d => d.id))].sort((a, b) => {
      return (titleLatestValues.get(b) || 0) - (titleLatestValues.get(a) || 0);
    });

    // Assign colors - use passed color map from parent for consistency, with local fallback
    const localTitleColorMap = new Map<string, string>();
    let colorIndex = 0;
    sortedIds.forEach((id) => {
      if (propsTitleColorMap?.has(id)) {
        localTitleColorMap.set(id, propsTitleColorMap.get(id)!);
      } else if (id === SONSTIGES_ID) {
        localTitleColorMap.set(id, SONSTIGES_COLOR);
      } else {
        localTitleColorMap.set(id, getSubTypeColor(colorIndex));
        colorIndex++;
      }
    });

    // Create chart data keyed by id
    // Always store both Soll and Ist values - they're needed when a title is selected
    const titleChartData = titleYears.map(year => {
      const useIst = titleYearsWithIst.has(year);
      const yearData: Record<string, number | null> = { year };
      titleData
        .filter(d => d.year === year)
        .forEach(d => {
          // Always store both values - they're needed when a title is selected
          yearData[`${d.id}_soll`] = convertValue(d.total_soll, year);
          yearData[`${d.id}_ist`] = (useIst && d.total_ist !== null) ? convertValue(d.total_ist, year) : null;
          // Also store combined value for multi-title stacked area mode.
          // total_soll can be null (SUM() of all-null rows). Must coalesce to 0
          // here — d3.stack's running sum turns a null into NaN, which then
          // corrupts every layer above it in the stack.
          const rawValue = useIst ? (d.total_ist ?? 0) : (d.total_soll ?? 0);
          yearData[d.id] = convertValue(rawValue, year);
        });
      return yearData;
    });

    // Fill missing values with zeros for stable stacking
    // - Soll and combined values: always fill with 0
    // - Ist values: only fill with 0 for years that have Ist data (in titleYearsWithIst)
    //   to avoid showing zeros for recent years where actuals don't exist yet
    const allTitleIds = sortedIds; // Already have all unique IDs
    const filledChartData = titleChartData.map(yearData => {
      const year = yearData.year as number;
      const yearHasIst = titleYearsWithIst.has(year);
      const filledData = { ...yearData };

      allTitleIds.forEach(id => {
        // Fill combined value with 0 if missing
        if (filledData[id] === undefined) {
          filledData[id] = 0;
        }
        // Fill Soll with 0 if missing
        if (filledData[`${id}_soll`] === undefined) {
          filledData[`${id}_soll`] = 0;
        }
        // Fill Ist with 0 only for years that have Ist data
        // (keeps null for Soll-only years like the latest 2 years)
        if (filledData[`${id}_ist`] === undefined) {
          filledData[`${id}_ist`] = yearHasIst ? 0 : null;
        }

        // For the selected title, fill null Ist with 0 for any year where Ist data exists globally
        // This ensures continuous Ist line (no gaps) except for soll-only years where no Ist data exists yet
        if (selectedTitle?.id === id && filledData[`${id}_ist`] === null && yearHasIst) {
          filledData[`${id}_ist`] = 0;
        }
      });

      return filledData;
    });

    // sortedTypes contains the IDs for area/line rendering
    const finalSortedTypes = sortedIds;

    return {
      years: titleYears,
      yearsWithIst: titleYearsWithIst,
      sollYears: titleSollYears,
      sortedTypes: finalSortedTypes,
      colorMap: localTitleColorMap,
      chartData: filledChartData,
      idMap: idToNameMap,  // id -> latest name (for legend display)
      idYearMap: idYearToNameMap,  // id -> year -> name (for year-specific tooltip)
      idYearMetaMap: idYearToMetaMap,  // id -> year -> {prefix, EP, KP, ...}
      idToTitelFunktionMap,
      idToPrefixMap,
    };
  }, [titleData, propsTitleColorMap, convertValue, selectedTitle, globalYearsWithIst]);

  const canPickSonstiges = !!(onToggleTitleBreakout && filters && drillDownType && apiClient);

  // A subtitle is a description plus a click hint; a locked chart (an embed)
  // shows only the description, since the reader can't click.
  const withHint = (text: string, hint: string) =>
    interactive ? `${text}${m.chart.hintSeparator}${hint}` : text;

  // Display name of a series. Category levels key by the German category
  // value (translated for display); the title level keys by id, and only the
  // synthetic Sonstiges bucket has a translatable name.
  const seriesName = (type: string): string => {
    if (drillDownSubType !== null && titleChartInfo) {
      const name = titleChartInfo.idMap.get(type) || type;
      return type === SONSTIGES_ID ? categoryLabel(name) : name;
    }
    return categoryLabel(type);
  };

  const handleAreaClick = (type: string) => {
    if (!interactive) return;
    if (drillDownSubType !== null) {
      // Level 3: title level - only handle if titleChartInfo exists
      if (!titleChartInfo) return; // Ignore clicks while loading/no data
      // SONSTIGES is a synthetic aggregate — instead of filtering the table,
      // clicking it opens the picker to break individual titles out of it.
      if (type === SONSTIGES_ID) {
        if (canPickSonstiges) {
          setPickerAnchor({ ...lastPointerRef.current });
        }
        return;
      }
      // Clicking on a title filters the table
      // `type` is now the id, look up the display name
      const displayName = titleChartInfo.idMap.get(type) || type;
      const titelFunktion = titleChartInfo.idToTitelFunktionMap.get(type);
      const prefix = titleChartInfo.idToPrefixMap.get(type);
      onFilterByTitle?.(type, displayName, titelFunktion, prefix);
    } else if (drillDownType && subChartInfo) {
      // Level 2: clicking on a sub-type drills down to titles
      if (!type) return;
      onDrillDownSub(type);
    } else {
      // Level 1: clicking on a main type drills down to sub-types
      onDrillDown(type);
    }
  };

  const handleBack = () => {
    onDrillUp();
    setHoveredArea(null);
  };


  // Select display data based on drill-down level
  // Compute effectiveSingleTitle: explicit selection OR single title from filters
  // Note: drillDownSubType can be empty string '' for Einnahmen (no sub-types), so check !== null
  // IMPORTANT: This useMemo must be called before any early returns to satisfy React's rules of hooks
  const { displayData, effectiveSingleTitle } = useMemo(() => {
    const baseDisplayData = (drillDownSubType !== null && titleChartInfo)
      ? titleChartInfo
      : drillDownType && subChartInfo
      ? subChartInfo
      : mainChartInfo;

    // Compute effective single title: explicit selection OR single title from filters
    let effectiveTitle: SelectedTitle | null = null;
    if (selectedTitle) {
      effectiveTitle = selectedTitle;
    } else if (drillDownSubType !== null && titleChartInfo && titleChartInfo.sortedTypes.length === 1) {
      const titleId = titleChartInfo.sortedTypes[0];
      const displayName = titleChartInfo.idMap.get(titleId) || titleId;
      effectiveTitle = {
        id: titleId,
        zweckbestimmung: displayName,
        titelFunktion: titleChartInfo.idToTitelFunktionMap.get(titleId),
        prefix: titleChartInfo.idToPrefixMap.get(titleId),
      };
    }

    // Filter chart data for single title visualization
    if (effectiveTitle && drillDownSubType !== null && titleChartInfo) {
      const titleId = effectiveTitle.id;
      const filteredChartData = titleChartInfo.chartData.map(d => ({
        year: d.year,
        [`${titleId}_soll`]: d[`${titleId}_soll`],
        [`${titleId}_ist`]: d[`${titleId}_ist`],
      }));
      return {
        displayData: { ...titleChartInfo, chartData: filteredChartData },
        effectiveSingleTitle: effectiveTitle,
      };
    }

    return { displayData: baseDisplayData, effectiveSingleTitle: effectiveTitle };
  }, [drillDownSubType, titleChartInfo, drillDownType, subChartInfo, mainChartInfo, selectedTitle]);

  // Close the per-point popover whenever the user navigates out of single-title mode.
  useEffect(() => {
    if (!effectiveSingleTitle || drillDownSubType === null) {
      setClickedPoint(null);
    }
  }, [effectiveSingleTitle, drillDownSubType]);

  // Close the Sonstiges picker when leaving the title level (there's no Sonstiges
  // to open it from elsewhere).
  useEffect(() => {
    if (drillDownSubType === null) setPickerAnchor(null);
  }, [drillDownSubType]);

  // The picker is a multi-select panel: breaking a title out no longer closes
  // it, so the user can promote several titles in one session and dismiss via
  // Escape / outside-click / the close (or "Fertig") button. The chart updates
  // live behind it — useBudgetData swaps the new series in without a spinner
  // flash (soft refetch), so the result is visible without closing the picker.

  // Smooth stacked areas WITHOUT the monotone overshoot. Rather than let
  // recharts spline each stacked layer's boundary (the difference of two
  // independent monotone splines is not overshoot-free and bleeds colour
  // sideways — see utils/resample.ts), we PCHIP-resample each rendered area
  // layer to sub-year points here and draw them with type="linear". PCHIP per
  // layer provably cannot overshoot, so each fill stays inside its own year
  // window; linear over the dense samples reproduces the smooth curve exactly.
  // The original integer-year knots survive intact, so tooltip/click can snap
  // the hovered x back to a real year. Single-title mode draws non-stacked
  // Ist/Soll lines (which need exact points and don't bleed) — skip it there.
  const denseChartData = useMemo<ResampleRow[]>(() => {
    if (effectiveSingleTitle) return displayData.chartData as ResampleRow[];
    // The dataKeys actually drawn as areas: sortedTypes, with Haushaltstechnik
    // expanded to its pre-split positive/negative halves.
    const areaKeys: string[] = [];
    for (const type of displayData.sortedTypes) {
      if (type === HAUSHALTSTECHNIK_TYPE) {
        areaKeys.push(HAUSHALTSTECHNIK_POS, HAUSHALTSTECHNIK_NEG);
      } else {
        areaKeys.push(type);
      }
    }
    return pchipResample(displayData.chartData as ResampleRow[], areaKeys, 12);
  }, [displayData, effectiveSingleTitle]);

  // Real-year rows in single-title mode (lines), dense rows for stacked areas.
  const chartRenderData = effectiveSingleTitle
    ? (displayData.chartData as ResampleRow[])
    : denseChartData;

  // stackOffset="sign" splits the stack into a positive half above y=0 and a
  // negative half below y=0. We only want that when we DELIBERATELY split a
  // layer into pos/neg halves — i.e. the Haushaltstechnik level-1 case which
  // pre-splits HHT into HAUSHALTSTECHNIK_POS / HAUSHALTSTECHNIK_NEG keys.
  //
  // Don't pick "sign" just because some random value happens to be negative.
  // Sub-type and title rows can carry tiny incidental negatives (refunds,
  // corrections — e.g. Ausgleichszahlungen 2023 has total_ist = -62k €).
  // Under "sign" those one-off negatives flip the entire layer to the
  // negative side of the baseline for that year, so the area visually swaps
  // position with everything else and the curve appears to "jump" from the
  // top of the stack down past zero. The user-visible symptom is exactly
  // that: areas switch their signs / change position.
  const stackOffset = useMemo<'sign' | 'none'>(() => {
    return displayData?.sortedTypes.includes(HAUSHALTSTECHNIK_TYPE) ? 'sign' : 'none';
  }, [displayData?.sortedTypes]);

  // Calculate max stacked value for consistent axis formatting. Computed over
  // the RENDERED rows (dense in stacked-area mode) so the YAxis fits the smooth
  // bulges — a fractional-x stacked sum can slightly exceed any single real
  // year's total when adjacent layers peak in different years.
  const maxStackedValue = useMemo(() => {
    if (!chartRenderData) return 0;
    return Math.max(...chartRenderData.map(d => {
      // Sum all numeric values except 'year' key for stacked area calculation
      return Object.entries(d)
        .filter(([key]) => key !== 'year')
        .reduce((sum, [, val]) => sum + (typeof val === 'number' ? val : 0), 0);
    }));
  }, [chartRenderData]);

  // Tooltip formatter — picks per-value units so a 500 Mio. slice in a Mrd.-scale
  // chart shows as "500 Mio. €" instead of "0,5 Mrd. €".
  const valueFormatter = useMemo(() => {
    return displayUnit === 'gdp_percent' || displayUnit === 'budget_percent'
      ? formatGdpPercentage
      : formatTooltipValue;
  }, [displayUnit]);

  // Tick formatter without units — used on axis ticks only
  const axisTickFormatter = useMemo(() => {
    return displayUnit === 'gdp_percent' || displayUnit === 'budget_percent'
      ? formatGdpPercentageTick
      : createAxisFormatter(maxStackedValue);
  }, [displayUnit, maxStackedValue]);

  const yAxisLabel = useMemo(() => {
    if (displayUnit === 'gdp_percent') return m.units.gdpPercent;
    if (displayUnit === 'budget_percent') return m.units.budgetPercent;
    return getAxisUnit(maxStackedValue).suffix;
  }, [displayUnit, maxStackedValue, m]);

  // Compute filter info text - MUST be before early returns to satisfy React's Rules of Hooks
  const filterInfoText = useMemo(() => {
    return buildFilterInfoText(activeFilters, filterOptions);
  }, [activeFilters, filterOptions]);

  // Flush-left y-axis (publishing chart engine): axis width = widest tick
  // label + 14px, chart margin.left = 0 → widest label flush with the title.
  // The returned value is also the plot's left edge — the unit caption and
  // the bottom legend anchor there, exactly like the engine.
  const yAxisWidth = useFlushLeftYAxisWidth(chartContainerRef, [displayData, displayUnit]);

  // Show loading state
  if (isLoading || (drillDownType && isLoadingSubData) || (drillDownSubType !== null && isLoadingTitleData)) {
    return (
      <Section ref={ref}>
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      </Section>
    );
  }

  // Show empty state if at level 3 but title data hasn't been processed yet
  if (drillDownSubType !== null && !titleChartInfo && !isLoadingTitleData) {
    return (
      <Section ref={ref}>
        <LoadingOverlay>
          <span>{m.chart.noTitleData}</span>
        </LoadingOverlay>
      </Section>
    );
  }

  // No years means the current filter combination returned no data. Without this
  // guard, the XAxis domain becomes [NaN, undefined] and recharts renders blank
  // with no message.
  if (displayData.years.length === 0) {
    return (
      <Section ref={ref}>
        <LoadingOverlay>
          <span>{m.chart.noDataForFilters}</span>
        </LoadingOverlay>
      </Section>
    );
  }

  // Build breadcrumb items
  const breadcrumbItems: { label: string; onClick?: () => void }[] = [];

  // Always show root - clickable when drilled down
  const rootLabel = budgetType === 'E' ? m.widget.rootRevenue : m.widget.rootExpenditure;
  breadcrumbItems.push({
    label: rootLabel,
    onClick: drillDownType ? onNavigateToRoot : undefined
  });

  if (drillDownType) {
    // Level 2+: show drill-down type (clickable if deeper)
    breadcrumbItems.push({
      label: categoryLabel(drillDownType),
      onClick: drillDownSubType !== null ? onNavigateToLevel2 : undefined
    });

    // Level 3+: show sub-type (skip empty string placeholders for Einnahmen)
    if (drillDownSubType && drillDownSubType !== '') {
      breadcrumbItems.push({
        label: categoryLabel(drillDownSubType),
        onClick: effectiveSingleTitle ? onDeselectTitle : undefined
      });
    }

    // Level 4: show selected title with prefix + titel_funktion
    if (effectiveSingleTitle) {
      const code = [effectiveSingleTitle.prefix, effectiveSingleTitle.titelFunktion].filter(Boolean).join(' ');
      const titleLabel = code ? `(${code}) ${effectiveSingleTitle.zweckbestimmung}` : effectiveSingleTitle.zweckbestimmung;
      breadcrumbItems.push({ label: titleLabel });
    }
  }

  const getChartSubtitle = () => {
    // A locked embed can't be clicked, so the click hint is left off rather
    // than telling the reader to do something that does nothing. Done once
    // here (withHint) so new instruction text can't leak in.
    if (effectiveSingleTitle) {
      return m.chart.areaSingleTitle;
    }
    if (drillDownSubType) {
      return withHint(
        m.chart.topTitles,
        canPickSonstiges ? m.chart.hintTitlesPick : m.chart.hintFilterTable,
      );
    }
    if (drillDownType) {
      return withHint(m.chart.subTypes, m.chart.hintDrill);
    }
    return withHint(m.chart.areaRoot, m.chart.hintDrill);
  };

  // Build chart breadcrumb for export. The export renders only the
  // breadcrumb path (title = deepest level, subtitle = parent path) — the
  // on-screen subtitle with its interaction hints is not exported.
  const chartBreadcrumb = breadcrumbItems.map(item => item.label);

  // Build legend items for export
  const getLegendItems = (): LegendItem[] => {
    // Single-title mode: show Ist/Soll legend
    if (effectiveSingleTitle && drillDownSubType !== null) {
      return [
        { name: m.common.ist, color: IST_LINE_COLOR },
        { name: m.common.soll, color: SOLL_LINE_COLOR },
      ];
    }

    // Multi-series mode: show all types with their colors
    return displayData.sortedTypes.map(type => {
      const displayName = seriesName(type);
      const color = displayData.colorMap.get(type) || '#ccc';
      const dashed = type === HAUSHALTSTECHNIK_TYPE;
      return { name: displayName, color, dashed };
    });
  };

  const getExportOptions = (): ChartExportOptions => ({
    breadcrumb: chartBreadcrumb,
    legend: getLegendItems(),
    filterInfo: filterInfoText || undefined,
    axisUnit: yAxisLabel,
    axisUnitX: yAxisWidth,
  });

  const handleExportPng = () => {
    const svgElement = chartContainerRef.current?.querySelector('svg.recharts-surface');
    if (svgElement) {
      exportChartAsPng(svgElement as SVGElement, getExportOptions());
    }
  };

  const handleExportSvg = () => {
    const svgElement = chartContainerRef.current?.querySelector('svg.recharts-surface');
    if (svgElement) {
      exportChartAsSvg(svgElement as SVGElement, getExportOptions());
    }
  };

  return (
    <Section ref={ref}>
      <FlexRow justify="space-between" align="flex-start" style={{ marginBottom: theme.spacing.lg }}>
        <FlexRow gap={theme.spacing.sm}>
          {interactive && (drillDownType || drillDownSubType) && (
            <BackButton variant="ghost" size="s" onClick={handleBack} aria-label={m.common.back}>
              <span className="icon" data-icon="arrow_back" />
            </BackButton>
          )}
          <div>
            <BreadcrumbContainer>
              {breadcrumbItems.map((item, index) => (
                <span key={index} style={{ display: 'flex', alignItems: 'center' }}>
                  {index > 0 && (
                    <BreadcrumbSeparator>
                      <span className="icon" data-icon="chevron_right" />
                    </BreadcrumbSeparator>
                  )}
                  <BreadcrumbItem
                    isLast={index === breadcrumbItems.length - 1}
                    isClickable={!!item.onClick}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbContainer>
            {filterInfoText && <FilterInfoLine>{filterInfoText}</FilterInfoLine>}
            {!filterInfoText && <CardSubtitle>{getChartSubtitle()}</CardSubtitle>}
          </div>
        </FlexRow>
        <FlexRow gap={theme.spacing.sm}>
          {activeFilter && onClearFilter && !drillDownType && drillDownSubType === null && (
            <ActiveFilterChip>
              <ChipText>{categoryLabel(activeFilter)}</ChipText>
              <ClearChipButton onClick={onClearFilter}>
                <span className="icon" data-icon="close" />
              </ClearChipButton>
            </ActiveFilterChip>
          )}
          {onEmbed && <EmbedButton onClick={onEmbed} />}
          {showExport && (
            <ExportDropdown onExportPng={handleExportPng} onExportSvg={handleExportSvg} />
          )}
        </FlexRow>
      </FlexRow>

      <ChartContainer
        ref={chartContainerRef}
        onPointerDownCapture={e => { lastPointerRef.current = { x: e.clientX, y: e.clientY }; }}
      >
        {/* Caption starts exactly at the axis line (= plot left edge), like
            the engine's unit caption (drawYAxis: x=0, text-anchor start). */}
        <AxisUnitLabel style={{ paddingLeft: yAxisWidth }}>{yAxisLabel}</AxisUnitLabel>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={chartRenderData}
            /* margin.left = 0: the dynamic YAxis width reserves exactly the
               widest tick label + 14px, putting that label's left edge flush
               with the title (engine flush-left rule). */
            margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
            onMouseLeave={() => setHoveredArea(null)}
            onClick={(state, event) => {
              if (!effectiveSingleTitle || drillDownSubType === null || !titleChartInfo || !apiClient) return;
              const activeLabel = (state as { activeLabel?: number | string } | undefined)?.activeLabel;
              const rawYear = typeof activeLabel === 'number' ? activeLabel : Number(activeLabel);
              if (!Number.isFinite(rawYear)) return;
              // Snap to a real year: in stacked-area mode the chart is fed dense
              // sub-year points, so activeLabel can be fractional. (Single-title
              // mode isn't densified, so this is a no-op there.)
              const year = Math.round(rawYear);
              const titleId = effectiveSingleTitle.id;
              const dataPoint = displayData.chartData.find(d => d.year === year);
              if (!dataPoint) return;
              const sollValue = (dataPoint[`${titleId}_soll`] as number | undefined) ?? 0;
              const istRaw = dataPoint[`${titleId}_ist`] as number | null | undefined;
              const istValue = istRaw === undefined ? null : istRaw;
              // Touch: first tap only previews (Recharts shows the tooltip);
              // a second tap on the same year opens the popover. Mirrors the
              // investments map's two-tap bubble behaviour so a single tap
              // doesn't show the tooltip AND the detail popover at once.
              if (isCoarsePointer() && armedYearRef.current !== year) {
                armedYearRef.current = year;
                if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
                // Long enough to comfortably land the deliberate second tap
                // before the preview disarms itself (matches InvestmentMap).
                armedTimerRef.current = setTimeout(() => { armedYearRef.current = null; }, 3500);
                return;
              }
              if (armedTimerRef.current) { clearTimeout(armedTimerRef.current); armedTimerRef.current = null; }
              armedYearRef.current = null;
              const native = (event as unknown as { clientX?: number; clientY?: number }) ?? {};
              const anchorX = typeof native.clientX === 'number' ? native.clientX : window.innerWidth / 2;
              const anchorY = typeof native.clientY === 'number' ? native.clientY : window.innerHeight / 2;
              setClickedPoint({ year, sollValue, istValue, anchorX, anchorY });
            }}
            stackOffset={stackOffset}
          >
            {/* dz-publishing-process chart engine: solid black axis lines
                with 6px outward tick marks, 8px tick-label padding; 13px
                black sans tick labels. The dashed grid is rendered AFTER the
                areas below — see comment there. */}
            <XAxis
              dataKey="year"
              type="number"
              /* +0.5 right padding lets the Soll-Werte ReferenceArea below
                 extend slightly past the last data tick — matches the Figma
                 overlay, which spills ~half a year past 2025. */
              domain={[displayData.years[0] - 0.5, displayData.years[displayData.years.length - 1] + 0.5]}
              ticks={displayData.years}
              tickFormatter={(value) => String(value)}
              tick={{ fontSize: 13, fill: '#000000', fontFamily: 'var(--sans-stack)' }}
              tickLine={{ stroke: '#000000' }}
              tickSize={6}
              tickMargin={8}
              axisLine={{ stroke: '#000000' }}
            />
            <YAxis
              tickFormatter={axisTickFormatter}
              tick={{ fontSize: 13, fill: '#000000', fontFamily: 'var(--sans-stack)' }}
              tickLine={{ stroke: '#000000' }}
              tickSize={6}
              tickMargin={8}
              axisLine={{ stroke: '#000000' }}
              width={yAxisWidth}
              allowDecimals={displayUnit !== 'gdp_percent' && displayUnit !== 'budget_percent'}
              domain={displayUnit === 'budget_percent' ? [0, 100] : undefined}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                // Stacked-area mode feeds the chart dense sub-year points, so
                // `label` can be fractional — snap it back to a real year before
                // any year-keyed lookup. (Single-title mode isn't densified.)
                const year = Math.round(label as number);
                const isIst = displayData.yearsWithIst.has(year);

                // Single-title mode (selected title): use IstSollTooltip
                if (effectiveSingleTitle && drillDownSubType !== null && titleChartInfo) {
                  const titleId = effectiveSingleTitle.id;
                  const yearNames = titleChartInfo.idYearMap.get(titleId);
                  const displayName = yearNames?.get(year) || titleChartInfo.idMap.get(titleId) || effectiveSingleTitle.zweckbestimmung;
                  const meta = titleChartInfo.idYearMetaMap.get(titleId)?.get(year);
                  const dataPoint = displayData.chartData.find(d => d.year === year);
                  const sollValue = dataPoint?.[`${titleId}_soll`] as number | undefined;
                  const istValue = dataPoint?.[`${titleId}_ist`] as number | null | undefined;
                  return (
                    <IstSollTooltip
                      year={year}
                      name={displayName}
                      sollValue={sollValue ?? 0}
                      istValue={istValue ?? null}
                      valueFormatter={valueFormatter}
                      meta={meta}
                    />
                  );
                }

                // payload values are interpolated at the (possibly fractional)
                // dense point. Read the REAL value for the snapped year from the
                // source rows instead, so the tooltip always shows true figures.
                const realRow = displayData.chartData.find(d => d.year === year);
                let entries = payload
                  .map(p => {
                    const dataKey = p.dataKey as string;
                    const realValue = realRow ? realRow[dataKey] : p.value;
                    // At title level, look up the year-specific name and metadata
                    let name = p.name as string;
                    let meta: TitleYearMeta | undefined;
                    if (drillDownSubType !== null && titleChartInfo) {
                      const yearNames = titleChartInfo.idYearMap.get(dataKey);
                      if (yearNames) {
                        name = yearNames.get(year) || name;
                      }
                      if (dataKey === SONSTIGES_ID) name = categoryLabel(name);
                      meta = titleChartInfo.idYearMetaMap.get(dataKey)?.get(year);
                    }
                    return {
                      name,
                      value: realValue as number,
                      color: p.color as string,
                      dataKey,
                      meta,
                    };
                  })
                  .filter(e => e.value !== undefined && e.value !== null);

                // Merge the sign-split Haushaltstechnik halves back into a single
                // signed tooltip row. See utils/haushaltstechnik.ts for the why.
                // The merged row carries its German key as name; show the display name.
                entries = mergeHaushaltstechnikEntries(entries).map(e =>
                  e.dataKey === HAUSHALTSTECHNIK_TYPE ? { ...e, name: categoryLabel(e.name) } : e,
                );
                return (
                  <MultiValueTooltip
                    year={year}
                    isIst={isIst}
                    entries={entries}
                    hoveredEntry={hoveredArea}
                    valueFormatter={valueFormatter}
                  />
                );
              }}
            />
            {(() => {
              // Single-title mode (selected title): render dual Ist/Soll lines
              if (effectiveSingleTitle && drillDownSubType !== null && titleChartInfo) {
                const titleId = effectiveSingleTitle.id;

                // Publishing chart engine lines: 2.5px stroke, secondary
                // (comparison) line dashed 4/4, no permanent point markers
                // (.line / .line.secondary in createChart.js). activeDot
                // stays as the interactive hover/click affordance.
                return [
                  <Line
                    key={`${budgetType}-soll`}
                    type="monotone"
                    dataKey={`${titleId}_soll`}
                    name={m.common.soll}
                    stroke={SOLL_LINE_COLOR}
                    strokeWidth={2.5}
                    strokeDasharray="4 4"
                    dot={false}
                    activeDot={{ r: 5, fill: SOLL_LINE_COLOR, stroke: SOLL_LINE_COLOR }}
                  />,
                  <Line
                    key={`${budgetType}-ist`}
                    type="monotone"
                    dataKey={`${titleId}_ist`}
                    name={m.common.ist}
                    stroke={IST_LINE_COLOR}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 6, fill: IST_LINE_COLOR, stroke: IST_LINE_COLOR }}
                    connectNulls={false}
                  />,
                ];
              }

              return displayData.sortedTypes.flatMap(type => {
                // At title level, type is an id - look up display name from idMap
                const displayName = seriesName(type);
                const color = displayData.colorMap.get(type);

                const isHaushaltstechnik = type === HAUSHALTSTECHNIK_TYPE;
                // Render HHT as two stacked Areas (positive + negative halves)
                // so its polygon meets the x-axis at sign-flip years instead of
                // slicing diagonally across the rest of the stack.
                const dataKeys = isHaushaltstechnik
                  ? [HAUSHALTSTECHNIK_POS, HAUSHALTSTECHNIK_NEG]
                  : [type];
                // Publishing chart engine look: fully opaque, flush areas with
                // no contour between distinct fills (Figma rule). Haushalts-
                // technik is the Figma exception: dashed black 1px outline, no fill.
                //
                // Smoothness comes from PCHIP-resampled data (denseChartData),
                // NOT from recharts' curve. type MUST stay "linear" here: a
                // recharts "monotone" stacked layer is (top spline − bottom
                // spline), two independent splines whose difference is not
                // overshoot-free, so a sparse extreme spike bleeds sideways over
                // its neighbour in the wrong colour. Linear over the dense PCHIP
                // points reproduces the smooth curve with every fill pinned
                // inside its own year window. (See denseChartData / resample.ts.)
                return dataKeys.map(dataKey => (
                  <Area
                    key={`${budgetType}-${dataKey}`}
                    type="linear"
                    dataKey={dataKey}
                    name={displayName}
                    stackId="1"
                    stroke={isHaushaltstechnik ? '#000000' : 'none'}
                    fill={color}
                    strokeWidth={isHaushaltstechnik ? 1 : 0}
                    strokeDasharray={isHaushaltstechnik ? '2 2' : undefined}
                    fillOpacity={
                      isHaushaltstechnik
                        ? (hoveredArea === null ? 0.05 : hoveredArea === type ? 0.12 : 0.03)
                        : (hoveredArea === null ? 1 : hoveredArea === type ? 1 : 0.3)
                    }
                    style={{ cursor: (!interactive || (drillDownSubType !== null && type === SONSTIGES_ID && !canPickSonstiges)) ? 'default' : 'pointer' }}
                    onMouseEnter={() => setHoveredArea(type)}
                    onMouseLeave={() => setHoveredArea(null)}
                    onClick={() => handleAreaClick(type)}
                  />
                ));
              });
            })()}
            {/* dz-publishing-process chart engine: horizontal-only grid,
                black 1px dashes (4px dash / 4px gap) at reduced opacity so
                the dashes read as a faint locating aid. The engine RAISES
                the helper grid above the data for stacked areas ("a locating
                aid that always sits ABOVE the data and is never occluded by
                it" — createChart.js); recharts honors JSX order, so the grid
                is declared after the opaque fills. */}
            <CartesianGrid strokeDasharray="4 4" stroke="#000000" strokeOpacity={0.5} vertical={false} />
            {/* Figma "Soll-Werte" overlay: flat translucent grey band with a
                small black label at the top-left. Rectangle 1:202 fill is
                rgba(217,217,217,0.5); label is Social S (13 px regular)
                black. The band spills ~half a year past the last data tick
                — paired with the +0.5 right-padding on the XAxis domain
                above, x2 = lastSollYear + 0.5 reproduces that overhang.
                Rendered AFTER the areas so it tints the (opaque) stack;
                pointer-events off so it never blocks area hover/click. */}
            {displayData.sollYears.length > 0 && !effectiveSingleTitle && (
              <ReferenceArea
                key={`soll-${displayData.sollYears[0]}-${displayData.sollYears[displayData.sollYears.length - 1]}`}
                x1={displayData.sollYears[0] - 0.5}
                x2={displayData.sollYears[displayData.sollYears.length - 1] + 0.5}
                fill="rgba(217, 217, 217, 0.5)"
                fillOpacity={1}
                style={{ pointerEvents: 'none' }}
                label={{
                  value: m.chart.sollBand,
                  position: 'insideTopLeft',
                  fill: '#000000',
                  fontSize: 13,
                  fontWeight: 400,
                  fontFamily: 'var(--sans-stack)',
                  offset: 8,
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        {/* Legend rendered OUTSIDE recharts so the plot keeps its full fixed
            height — wrapping legend rows grow the section downward instead of
            shrinking the chart (engine behavior: plot size is fixed, legend
            space is added below). Flush-left with the title edge. */}
        {effectiveSingleTitle && drillDownSubType !== null ? (
          <LegendContainer>
            <LegendItem isClickable={false}>
              <LegendDot color={IST_LINE_COLOR} />
              <LegendLabel>{m.common.ist}</LegendLabel>
            </LegendItem>
            <LegendItem isClickable={false}>
              <LegendDot color={SOLL_LINE_COLOR} />
              <LegendLabel>{m.common.soll}</LegendLabel>
            </LegendItem>
          </LegendContainer>
        ) : (
          <LegendContainer>
            {displayData.sortedTypes.map(type => {
              const displayName = seriesName(type);
              const color = displayData.colorMap.get(type);
              const isSonstiges = drillDownSubType !== null && type === SONSTIGES_ID;
              const isBrokenOut = drillDownSubType !== null && !!brokenOutTitleIds?.includes(type);
              return (
                <LegendItem
                  key={type}
                  dimmed={hoveredArea !== null && hoveredArea !== type}
                  isClickable={interactive && (isSonstiges ? canPickSonstiges : true)}
                  onClick={() => handleAreaClick(type)}
                  title={isSonstiges && canPickSonstiges ? m.chart.showSonstigesTitles : undefined}
                >
                  <LegendDot
                    color={color || '#ccc'}
                    dashed={type === HAUSHALTSTECHNIK_TYPE}
                  />
                  <LegendLabel>{displayName}</LegendLabel>
                  {isSonstiges && canPickSonstiges && (
                    <LegendChevron aria-hidden="true">
                      <span className="icon" data-icon="chevron_right" />
                    </LegendChevron>
                  )}
                  {isBrokenOut && onToggleTitleBreakout && (
                    <LegendRemove
                      role="button"
                      aria-label={m.chart.backToSonstigesAria(displayName)}
                      title={m.chart.backToSonstiges}
                      onClick={e => { e.stopPropagation(); onToggleTitleBreakout(type); }}
                    >
                      <span className="icon" data-icon="close" />
                    </LegendRemove>
                  )}
                </LegendItem>
              );
            })}
          </LegendContainer>
        )}
      </ChartContainer>
      {clickedPoint && effectiveSingleTitle && drillDownSubType !== null && titleChartInfo && apiClient && (() => {
        const titleId = effectiveSingleTitle.id;
        const yearNames = titleChartInfo.idYearMap.get(titleId);
        const displayName = yearNames?.get(clickedPoint.year)
          || titleChartInfo.idMap.get(titleId)
          || effectiveSingleTitle.zweckbestimmung;
        const meta = titleChartInfo.idYearMetaMap.get(titleId)?.get(clickedPoint.year);
        return (
          <TitlePointPopover
            apiClient={apiClient}
            titleId={titleId}
            budgetType={budgetType}
            year={clickedPoint.year}
            name={displayName}
            sollValue={clickedPoint.sollValue}
            istValue={clickedPoint.istValue}
            meta={meta}
            valueFormatter={valueFormatter}
            anchorX={clickedPoint.anchorX}
            anchorY={clickedPoint.anchorY}
            onClose={() => setClickedPoint(null)}
          />
        );
      })()}
      {pickerAnchor && drillDownType && filters && onToggleTitleBreakout && (
        <SonstigesTitlePicker
          apiClient={apiClient!}
          filters={filters}
          parentType={drillDownType}
          subType={drillDownSubType}
          brokenOutTitleIds={brokenOutTitleIds ?? []}
          onToggle={onToggleTitleBreakout}
          onClose={() => setPickerAnchor(null)}
          anchorX={pickerAnchor.x}
          anchorY={pickerAnchor.y}
          valueFormatter={valueFormatter}
        />
      )}
    </Section>
  );
});
